import { Link } from 'gatsby';
import React from 'react';
import { ArrowRight } from 'lucide-react';
import styled from '../../styled';
import { Headline } from '../Headline';
import { Reveal } from '../motion';
import { NewBadge } from '../NewBadge';
import { CommerceLoopPanel } from './CommerceLoopPanel';

const SectionWrapper = styled('section')`
  background: linear-gradient(180deg, #fff 0%, #f4f8fc 12%, #f4f8fc 88%, #fff 100%);
  background-color: #f4f8fc;
  position: relative;
  padding: 100px 0 80px;

  /* Subtle dot grid, faded at the edges */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: radial-gradient(
      rgba(14, 28, 43, 0.07) 1px,
      transparent 1px
    );
    background-size: 22px 22px;
    mask-image: radial-gradient(
      ellipse 80% 70% at 50% 45%,
      black 30%,
      transparent 100%
    );
    pointer-events: none;
  }

  > * {
    position: relative;
  }

  @media (max-width: 960px) {
    padding: 72px 0 56px;
  }
`;

const SectionHeader = styled('div')`
  text-align: center;
  margin-bottom: 16px;
  padding: 0 24px;
`;

const SectionDescription = styled('p')`
  font-size: 18px;
  line-height: 1.6;
  color: #4f4f4f;
  margin-top: 16px;
  max-width: 720px;
  margin-left: auto;
  margin-right: auto;
`;

const SubsectionHeader = styled('div')`
  text-align: center;
  margin-top: 40px;
  padding: 0 24px;
`;

const LinkCards = styled('div')`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  max-width: 920px;
  margin: 24px auto 0;
  padding: 0 24px;

  /* Stretch the reveal wrappers so all cards share the same height */
  > * {
    height: 100%;
  }

  @media (max-width: 720px) {
    grid-template-columns: 1fr;
    gap: 12px;
    padding: 0 16px;
  }
`;

const LinkCard = styled(Link)`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;
  box-sizing: border-box;
  padding: 20px;
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  text-decoration: none;
  transition: border-color 0.2s, box-shadow 0.2s;

  &:hover {
    border-color: #1890ff;
    box-shadow: 0 8px 24px rgba(24, 144, 255, 0.1);
  }

  h3 {
    font-size: 18px;
    font-weight: 700;
    color: #0e1c2b;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
  }

  p {
    font-size: 14px;
    line-height: 1.5;
    color: #64748b;
    margin: 0;
    flex: 1;
  }
`;

const CardLink = styled('span')`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #1890ff;
`;

const solutions = [
  {
    title: 'Unify',
    slug: 'unify',
    description:
      'Aggregate affiliate data from 100+ networks into one source of truth.',
    new: false
  },
  {
    title: 'Optimize',
    slug: 'optimize',
    description:
      'Tie revenue to content, channels, and audiences. See where to scale.',
    new: false
  },
  {
    title: 'Amplify',
    slug: 'amplify',
    description:
      'Sponsorship and yield optimization campaigns under one roof, powered by AI recommendations.',
    new: true
  }
];

export const CommerceLoopSection = () => {
  return (
    <SectionWrapper id="publisher-solutions">
      <SectionHeader>
        <Reveal>
          <Headline component="h2">Publisher Solutions</Headline>
          <SectionDescription>
            Drive growth across diverse monetization models and channels with a
            single platform: unified data, editorial optimization, and
            campaigns, with AI working across all three.
          </SectionDescription>
        </Reveal>
      </SectionHeader>

      <CommerceLoopPanel />

      <SubsectionHeader>
        <Reveal>
          <Headline component="h2">Build your dream publisher platform</Headline>
          <SectionDescription>
            Use exactly what you need to accelerate your commerce business.
          </SectionDescription>
        </Reveal>
      </SubsectionHeader>

      <LinkCards>
        {solutions.map((solution, index) => (
          <Reveal key={solution.slug} delay={index * 80}>
            <LinkCard to={`/product/${solution.slug}/`}>
              <h3>
                {solution.title}
                {solution.new && <NewBadge>New</NewBadge>}
              </h3>
              <p>{solution.description}</p>
              <CardLink>
                Learn more <ArrowRight size={16} />
              </CardLink>
            </LinkCard>
          </Reveal>
        ))}
      </LinkCards>
    </SectionWrapper>
  );
};
